import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import {
	ApplyMapperTemplateRequest,
	ApplyMapperTemplateResponse,
	ApplyTransferByKeySettingResponse,
	GetSamplePayloadResponse,
	MapperTestRunResult,
	MapperAvailabilityResponse,
	MapperExpressionFunctionDefinition,
	MapperTransferByKeySetting,
    MapperProfileSummary,
    MappingDefinition,
    MappingEntityDescriptor,
    MappingEntitySchema,
	MappingPreviewRequest,
	MappingPreviewResponse,
	StartMapperTestRunRequest,
	StartMapperTestRunResponse,
	SetActiveMapperProfileRequest,
    MappingSchemaConfiguration
} from '../models/link-generic-mapper.models';

type MapperApiCacheOptions = {
  useCache?: boolean;
  forceRefresh?: boolean;
};

type MapperApiCacheEntry<T> = {
  expiresAt: number;
  value: T;
};

@Injectable({ providedIn: 'root' })
export class LinkGenericMapperApiService {
  private readonly http = inject(HttpClient);
	private readonly metadataCachePrefix = 'al3.link-generic-mapper.metadata.v7.';
  private readonly metadataCacheTtlMs = 30 * 60 * 1000;
  private readonly metadataCache = new Map<string, unknown>();
	private readonly entitySchemaCache = new Map<string, MappingEntitySchema>();
	private readonly inFlightEntitySchemas = new Map<string, Observable<MappingEntitySchema>>();

  getAvailability(linkId: number): Observable<MapperAvailabilityResponse> {
    return this.http.get<MapperAvailabilityResponse>('/api/link-mapper/enabled', {
      params: {
        linkId
      }
    });
  }

  getSourceEntities(linkId: number, options?: MapperApiCacheOptions): Observable<MappingEntityDescriptor[]> {
    return this.getCachedResponse(
      this.buildMetadataCacheKey('source-entities', linkId.toString()),
      () => this.http.get<MappingEntityDescriptor[]>('/api/link-mapper/source-entities', {
        params: {
          linkId
        }
      }),
      options
    );
  }

  getTargetEntities(linkId: number, options?: MapperApiCacheOptions): Observable<MappingEntityDescriptor[]> {
    return this.getCachedResponse(
      this.buildMetadataCacheKey('target-entities', linkId.toString()),
      () => this.http.get<MappingEntityDescriptor[]>('/api/link-mapper/target-entities', {
        params: {
          linkId
        }
      }),
      options
    );
  }

  getConfiguration(
    linkId: number,
    sourceEntity: string,
    targetEntity: string,
    options?: MapperApiCacheOptions
  ): Observable<MappingSchemaConfiguration> {
    return this.getCachedResponse(
      this.buildConfigurationCacheKey(linkId, sourceEntity, targetEntity),
      () => this.http.get<MappingSchemaConfiguration>('/api/link-mapper/configuration', {
        params: {
          linkId,
          sourceEntity,
          targetEntity
        }
      }),
      options
    );
  }

	getExpressionFunctions(): Observable<MapperExpressionFunctionDefinition[]> {
		return this.http.get<MapperExpressionFunctionDefinition[]>('/api/link-mapper/expression-functions');
	}

	getTransferByKeySettingState(linkId: number, entityName: string): Observable<MapperTransferByKeySetting | null> {
		return this.http.get<MapperTransferByKeySetting | null>('/api/link-mapper/transfer-by-key-setting-state', {
			params: {
				linkId,
				entityName
			}
		});
	}

  hasCachedMetadata(): boolean {
    this.pruneExpiredMetadataCache();

    if (this.metadataCache.size > 0) {
      return true;
    }

    const storage = this.getSessionStorage();
    if (!storage) {
      return false;
    }

    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(this.metadataCachePrefix)) {
        return true;
      }
    }

    return false;
  }

  clearMetadataCache(): void {
    this.metadataCache.clear();
		this.entitySchemaCache.clear();
		this.inFlightEntitySchemas.clear();

    const storage = this.getSessionStorage();
    if (!storage) {
      return;
    }

    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(this.metadataCachePrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => storage.removeItem(key));
  }

  cacheConfiguration(linkId: number, sourceEntity: string, targetEntity: string, configuration: MappingSchemaConfiguration): void {
		const cachedConfiguration: MappingSchemaConfiguration = {
			...configuration,
			sampleSourcePayload: null,
			samplePayloadRetrieved: false,
			samplePayloadStatusMessage: null
		};

    this.writeMetadataCacheEntry(
      this.buildConfigurationCacheKey(linkId, sourceEntity, targetEntity),
			cachedConfiguration
    );
  }

  getSampleSourcePayload(
    linkId: number,
    entityName: string,
    inputs: Record<string, string | null>
  ): Observable<GetSamplePayloadResponse> {
    return this.http.post<GetSamplePayloadResponse>('/api/link-mapper/sample-source-payload', {
      linkId,
      entityName,
      inputs
    });
  }

	applyTransferByKeySetting(
		linkId: number,
		entityName: string,
		inputs: Record<string, string | null>
	): Observable<ApplyTransferByKeySettingResponse> {
		return this.http.post<ApplyTransferByKeySettingResponse>('/api/link-mapper/apply-transfer-by-key-setting', {
			linkId,
			entityName,
			inputs
		});
	}

  previewMapping(
    mapping: MappingDefinition,
    sampleSourcePayload: any,
    sourceSchema?: MappingEntitySchema | null,
    targetSchema?: MappingEntitySchema | null
  ): Observable<MappingPreviewResponse> {
    const request: MappingPreviewRequest = {
      mapping,
      sampleSourcePayload,
      sourceSchema: sourceSchema ?? null,
      targetSchema: targetSchema ?? null
    };

    return this.http.post<MappingPreviewResponse>('/api/link-mapper/preview', request);
  }

  validateMapping(
    mapping: MappingDefinition,
    sampleSourcePayload: any,
    sourceSchema?: MappingEntitySchema | null,
    targetSchema?: MappingEntitySchema | null
  ): Observable<MappingPreviewResponse> {
    const request: MappingPreviewRequest = {
      mapping,
      sampleSourcePayload,
      sourceSchema: sourceSchema ?? null,
      targetSchema: targetSchema ?? null
    };

    return this.http.post<MappingPreviewResponse>('/api/link-mapper/validate', request);
  }

  saveMapping(mapping: MappingDefinition): Observable<void> {
    return this.http.post<void>('/api/link-mapper/mappings', mapping);
  }

	getMappingTemplates(linkId: number, sourceEntity: string, targetEntity: string): Observable<MapperProfileSummary[]> {
		return this.http.get<MapperProfileSummary[]>('/api/link-mapper/templates', {
			params: {
				linkId,
				sourceEntity,
				targetEntity
			}
		});
	}

	getMappingTemplate(
		linkId: number,
		sourceEntity: string,
		targetEntity: string,
		templateName: string
	): Observable<MappingDefinition> {
		return this.http.get<MappingDefinition>('/api/link-mapper/templates/mapping', {
			params: {
				linkId,
				sourceEntity,
				targetEntity,
				templateName
			}
		});
	}

	saveMappingTemplate(mapping: MappingDefinition): Observable<void> {
		return this.http.post<void>('/api/link-mapper/templates', mapping);
	}

  getMappingProfile(
    linkId: number,
    sourceEntity: string,
    targetEntity: string,
    profileName: string
  ): Observable<MappingDefinition> {
    return this.http.get<MappingDefinition>('/api/link-mapper/mappings', {
      params: {
        linkId,
        sourceEntity,
        targetEntity,
        profileName
      }
    });
  }

	setActiveMappingProfile(request: SetActiveMapperProfileRequest): Observable<void> {
		return this.http.post<void>('/api/link-mapper/mappings/active', request);
	}

	applyTemplateForBasicSetup(request: ApplyMapperTemplateRequest): Observable<ApplyMapperTemplateResponse> {
		return this.http.post<ApplyMapperTemplateResponse>('/api/link-mapper/basic/apply-template', request);
	}

	startTestRun(request: StartMapperTestRunRequest): Observable<StartMapperTestRunResponse> {
		return this.http.post<StartMapperTestRunResponse>('/api/link-mapper/start-test-run', request);
	}

	getTestRunResults(linkId: number, sessionId: string, sourceEntity: string, targetEntity: string): Observable<MapperTestRunResult> {
		return this.http.get<MapperTestRunResult>('/api/link-mapper/test-run-results', {
			params: {
				linkId,
				sessionId,
				sourceEntity,
				targetEntity
			}
		});
	}

  getEntitySchema(
    linkId: number,
    entityName: string,
    side: 'source' | 'target' = 'target',
    inputs?: Record<string, string | null>
  ): Observable<MappingEntitySchema> {
		const normalizedInputs = Object.entries(inputs ?? {})
			.sort(([left], [right]) => left.localeCompare(right));
		const key = JSON.stringify([linkId, side, entityName.toLowerCase(), normalizedInputs]);
		const cached = this.entitySchemaCache.get(key);
		if (cached) {
			return of(this.cloneValue(cached));
		}

		const inFlight = this.inFlightEntitySchemas.get(key);
		if (inFlight) {
			return inFlight.pipe(map(schema => this.cloneValue(schema)));
		}

		const request = this.http.get<MappingEntitySchema>(
			'/api/link-mapper/schema',
			{
				params: {
					linkId,
					entityName,
					side,
					...(inputs ?? {})
				}
			}
		).pipe(
			tap(schema => this.entitySchemaCache.set(key, this.cloneValue(schema))),
			finalize(() => this.inFlightEntitySchemas.delete(key)),
			shareReplay({ bufferSize: 1, refCount: false })
		);

		this.inFlightEntitySchemas.set(key, request);
		return request.pipe(map(schema => this.cloneValue(schema)));
  }

  private getCachedResponse<T>(
    key: string,
    requestFactory: () => Observable<T>,
    options?: MapperApiCacheOptions
  ): Observable<T> {
    const useCache = options?.useCache === true;
    const forceRefresh = options?.forceRefresh === true;

    if (useCache && !forceRefresh) {
      const cachedValue = this.readMetadataCacheEntry<T>(key);
      if (cachedValue !== null) {
        return of(cachedValue);
      }
    }

    return requestFactory().pipe(
      tap(value => {
        if (!useCache) {
          return;
        }

        this.writeMetadataCacheEntry(key, value);
      })
    );
  }

  private buildConfigurationCacheKey(linkId: number, sourceEntity: string, targetEntity: string): string {
    return this.buildMetadataCacheKey('configuration', linkId.toString(), sourceEntity, targetEntity);
  }

  private buildMetadataCacheKey(...parts: string[]): string {
    return `${this.metadataCachePrefix}${parts.join('::')}`;
  }

  private readMetadataCacheEntry<T>(key: string): T | null {
    this.pruneExpiredMetadataCache();

    if (this.metadataCache.has(key)) {
      return this.cloneValue(this.metadataCache.get(key) as T);
    }

    const storage = this.getSessionStorage();
    if (!storage) {
      return null;
    }

    const rawEntry = storage.getItem(key);
    if (!rawEntry) {
      return null;
    }

    try {
      const entry = JSON.parse(rawEntry) as MapperApiCacheEntry<T>;
      if (!entry || typeof entry.expiresAt !== 'number' || entry.expiresAt <= Date.now()) {
        storage.removeItem(key);
        return null;
      }

      this.metadataCache.set(key, this.cloneValue(entry.value));
      return this.cloneValue(entry.value);
    } catch {
      storage.removeItem(key);
      return null;
    }
  }

  private writeMetadataCacheEntry<T>(key: string, value: T): void {
    const clonedValue = this.cloneValue(value);
    this.metadataCache.set(key, clonedValue);

    const storage = this.getSessionStorage();
    if (!storage) {
      return;
    }

    const entry: MapperApiCacheEntry<T> = {
      expiresAt: Date.now() + this.metadataCacheTtlMs,
      value: clonedValue
    };

    try {
      storage.setItem(key, JSON.stringify(entry));
    } catch {
      this.metadataCache.delete(key);
    }
  }

  private pruneExpiredMetadataCache(): void {
    const storage = this.getSessionStorage();
    if (!storage) {
      return;
    }

    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key?.startsWith(this.metadataCachePrefix)) {
        continue;
      }

      const rawEntry = storage.getItem(key);
      if (!rawEntry) {
        keysToRemove.push(key);
        continue;
      }

      try {
        const entry = JSON.parse(rawEntry) as MapperApiCacheEntry<unknown>;
        if (!entry || typeof entry.expiresAt !== 'number' || entry.expiresAt <= now) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      storage.removeItem(key);
      this.metadataCache.delete(key);
    });
  }

  private getSessionStorage(): Storage | null {
    try {
      if (typeof sessionStorage === 'undefined') {
        return null;
      }

      return sessionStorage;
    } catch {
      return null;
    }
  }

  private cloneValue<T>(value: T): T {
    if (value == null) {
      return value;
    }

    return JSON.parse(JSON.stringify(value)) as T;
  }
}
